import { Card, Form, Input, Switch, Select, Button, Spin, message, Divider, Tag, Table, Empty, Alert } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, LinkOutlined, LoadingOutlined } from "@ant-design/icons";
import { useAuth } from "~/store";
import { useOrganization, useUpdateOrganization } from "~/services/organization";
import { useGetBusinessAddresses } from "~/services/business-addresses";
import { SubscriptionTier } from "~/utils/constants";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useSearchParams } from "react-router-dom";
import type { TBusinessAddress } from "~/types/business-address";
import { startStripeConnect, getStripeConnectStatus, refreshStripeConnectLink } from "~/services/billing/billing.api";

export const OrganizationSettings = () => {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const { data: org, isLoading } = useOrganization(orgId || "");
  const { data: addresses = [], isLoading: loadingAddresses } = useGetBusinessAddresses(true);
  const updateOrg = useUpdateOrganization();
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();

  // Stripe Connect state
  const [stripeStatus, setStripeStatus] = useState<{
    connected: boolean;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    accountId: string | null;
    isPlatformOwner?: boolean;
  } | null>(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeStatusLoading, setStripeStatusLoading] = useState(true);

  useEffect(() => {
    if (org) {
      form.setFieldsValue(org);
    }
  }, [org, form]);

  // Load Stripe Connect status
  useEffect(() => {
    const loadStripeStatus = async () => {
      try {
        const status = await getStripeConnectStatus();
        setStripeStatus(status);
      } catch {
        // Stripe not configured on backend
        setStripeStatus(null);
      } finally {
        setStripeStatusLoading(false);
      }
    };
    loadStripeStatus();
  }, []);

  // Handle return from Stripe onboarding
  useEffect(() => {
    const stripeParam = searchParams.get("stripe");
    if (stripeParam === "success") {
      message.success("Stripe account connected! Checking status...");
      setStripeStatusLoading(true);
      getStripeConnectStatus().then((status) => {
        setStripeStatus(status);
        setStripeStatusLoading(false);
      });
    } else if (stripeParam === "refresh") {
      message.info("Stripe onboarding was interrupted. You can resume below.");
    }
  }, [searchParams]);

  if (isLoading) return <Spin className="flex justify-center mt-20" />;
  if (!org) return <div className="p-6">No organization found</div>;

  const handleSave = async (values: any) => {
    try {
      await updateOrg.mutateAsync({ id: org.id, body: values });
      message.success("Organization updated");
    } catch {
      message.error("Failed to update organization");
    }
  };

  const handleConnectStripe = async () => {
    setStripeLoading(true);
    try {
      const { url } = await startStripeConnect();
      window.location.href = url;
    } catch (e: any) {
      message.error(e?.response?.data?.message || "Failed to start Stripe onboarding");
      setStripeLoading(false);
    }
  };

  const handleResumeOnboarding = async () => {
    setStripeLoading(true);
    try {
      const { url } = await refreshStripeConnectLink();
      window.location.href = url;
    } catch (e: any) {
      message.error(e?.response?.data?.message || "Failed to get onboarding link");
      setStripeLoading(false);
    }
  };

  const isSuperAdmin = user?.role === "super_admin" || user?.role === "system_admin";

  const addressColumns = [
    {
      title: "Location",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: TBusinessAddress) => (
        <div>
          <span className="font-medium">{name}</span>
          <Tag className="ml-2" color={record.addressType === "warehouse" ? "blue" : record.addressType === "office" ? "green" : "orange"}>
            {record.addressType}
          </Tag>
        </div>
      ),
    },
    {
      title: "Address",
      key: "address",
      render: (_: any, record: TBusinessAddress) => (
        <span className="text-sm text-gray-600">
          {record.address}{record.address2 ? `, ${record.address2}` : ""}, {record.city}, {record.state} {record.zip}
        </span>
      ),
    },
    {
      title: "Contact",
      key: "contact",
      render: (_: any, record: TBusinessAddress) => (
        <div className="text-sm">
          {record.contactPerson && <div>{record.contactPerson}</div>}
          {record.phone && <div className="text-gray-500">{record.phone}</div>}
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-xl font-bold">Organization Settings</h1>
        {org.isPlatformOwner && (
          <Tag color="black" className="text-xs font-semibold">Platform Owner</Tag>
        )}
      </div>

      <Card className="mb-6">
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Divider orientation="left" className="!mt-0">General</Divider>

          <Form.Item name="name" label="Organization Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="slug" label="Slug (URL identifier)">
              <Input disabled />
            </Form.Item>
            <Form.Item name="timezone" label="Timezone">
              <Select
                options={[
                  { value: "America/New_York", label: "Eastern (ET)" },
                  { value: "America/Chicago", label: "Central (CT)" },
                  { value: "America/Denver", label: "Mountain (MT)" },
                  { value: "America/Los_Angeles", label: "Pacific (PT)" },
                ]}
              />
            </Form.Item>
          </div>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Brief description of your organization..." />
          </Form.Item>

          <Divider orientation="left">Contact Information</Divider>

          <div className="grid grid-cols-3 gap-4">
            <Form.Item name="phone" label="Phone">
              <Input placeholder="(555) 123-4567" />
            </Form.Item>
            <Form.Item name="email" label="Email">
              <Input placeholder="info@company.com" />
            </Form.Item>
            <Form.Item name="website" label="Website">
              <Input placeholder="https://company.com" />
            </Form.Item>
          </div>

          <Divider orientation="left">Plan & Network</Divider>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="subscriptionTier" label="Plan">
              <Select
                disabled={!org.isPlatformOwner}
                options={Object.values(SubscriptionTier).map((t) => ({
                  value: t,
                  label: t.charAt(0).toUpperCase() + t.slice(1),
                }))}
              />
            </Form.Item>
            <Form.Item name="networkEnabled" label="Enable Network" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>

          <p className="text-xs text-gray-500 mb-6">
            When enabled, your organization can share shuttle routes and receive job referrals from other art handling companies on the platform.
          </p>

          <Button type="primary" htmlType="submit" loading={updateOrg.isPending}
            className="bg-black hover:bg-gray-800 border-black">
            Save Changes
          </Button>
        </Form>
      </Card>

      {/* Stripe Connect */}
      {isSuperAdmin && (
        <Card className="mb-6" title="Payments — Stripe Connect">
          {stripeStatusLoading ? (
            <div className="flex items-center gap-2 py-4">
              <LoadingOutlined />
              <span className="text-gray-500">Checking Stripe connection...</span>
            </div>
          ) : stripeStatus?.connected && stripeStatus.chargesEnabled ? (
            <div>
              <Alert
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
                message={stripeStatus.isPlatformOwner ? "Platform Stripe Account" : "Stripe Connected"}
                description={
                  <div className="text-sm">
                    <p>
                      {stripeStatus.isPlatformOwner
                        ? "Using the platform Stripe account directly. Payment links are created on your main Stripe account."
                        : "Your Stripe account is connected and ready to accept payments."}
                    </p>
                    {!stripeStatus.isPlatformOwner && (
                      <div className="flex gap-4 mt-2">
                        <span>
                          Charges: {stripeStatus.chargesEnabled
                            ? <Tag color="green">Enabled</Tag>
                            : <Tag color="red">Disabled</Tag>}
                        </span>
                        <span>
                          Payouts: {stripeStatus.payoutsEnabled
                            ? <Tag color="green">Enabled</Tag>
                            : <Tag color="orange">Pending</Tag>}
                        </span>
                      </div>
                    )}
                  </div>
                }
              />
            </div>
          ) : stripeStatus?.accountId ? (
            <div>
              <Alert
                type="warning"
                showIcon
                icon={<CloseCircleOutlined />}
                message="Stripe Onboarding Incomplete"
                description="Your Stripe account was created but onboarding isn't finished. Complete it to start accepting payments."
                className="mb-4"
              />
              <Button
                type="primary"
                icon={<LinkOutlined />}
                onClick={handleResumeOnboarding}
                loading={stripeLoading}
                className="bg-black hover:bg-gray-800 border-black"
              >
                Resume Stripe Onboarding
              </Button>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Connect your Stripe account to accept payments from clients through invoices.
                Payment links will be created on your Stripe account, and funds go directly to you.
              </p>
              <Button
                type="primary"
                icon={<LinkOutlined />}
                onClick={handleConnectStripe}
                loading={stripeLoading}
                className="bg-black hover:bg-gray-800 border-black"
              >
                Connect Stripe Account
              </Button>
            </div>
          )}
        </Card>
      )}

      <Card
        title="Business Locations"
        extra={
          <Link to="/operations/backend-setup?tab=business-addresses" className="text-xs text-gray-500 hover:text-black">
            Manage in Backend Setup
          </Link>
        }
      >
        {loadingAddresses ? (
          <Spin className="flex justify-center py-8" />
        ) : addresses.length === 0 ? (
          <Empty
            description={
              <span>
                No business addresses configured.{" "}
                <Link to="/operations/backend-setup?tab=business-addresses" className="text-blue-600">
                  Add one in Backend Setup
                </Link>
              </span>
            }
          />
        ) : (
          <Table
            dataSource={addresses}
            columns={addressColumns}
            rowKey="id"
            size="small"
            pagination={false}
          />
        )}
      </Card>
    </div>
  );
};
